import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
    Button,
    Callout,
    FormField,
    FormFieldMessage,
    Input,
    OptionCard,
    OptionCardGroup,
    PageHeader,
    Select,
    SectionCard,
} from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { AppProvisioningResultModal } from '../AppDetail/AppProvisioningResultModal';
import { useProvisioningResult } from '../AppDetail/useProvisioningResult';
import { APP_TYPE_OPTIONS, useCreateAppForm } from './useCreateAppForm';
import { AppUriListField } from './AppUriListField';
import styles from './AppsCreatePage.module.css';

const AFTER_SAVE_STEP_KEYS = [1, 2, 3, 4, 5] as const;

/**
 * EAA-1846 (design frame "Thêm ứng dụng — trang"): `/console/apps/new` — the
 * routed replacement for `CreateAppModal`. The design moved this flow from a
 * 520px modal to a full page with a "Sau khi lưu sẽ ra sao" side panel; the
 * fields themselves are unchanged (`useCreateAppForm` is reused as-is), only
 * the chrome and the success action differ — `onCreated` here navigates back
 * to the registry list instead of closing a dialog.
 *
 * Field grouping/copy follows the screenshot verbatim (new `createPage.*` i18n
 * keys), but every field `CreateAppModal` had — including the ones the
 * screenshot's frame cut off before showing (icon_url, post-logout URIs,
 * org-scope radio, supports_role_management) — is carried over unchanged so
 * the page loses no capability the modal had.
 *
 * Layout: reuses the established `.portal-admin-form` auto-fit grid (see
 * 01-controls.css) for each card's own fields — its "non-label children span
 * the full row" rule is what turns the always-on hints under Mã/Loại/Audience
 * into deliberate row breaks, producing the screenshot's Tên+Mã / Loại+Audience
 * / Web+API pairs without any new field-grid CSS. The only genuinely new CSS
 * is the outer 2-column page split (main content + "Sau khi lưu" side panel),
 * added to 10-apps.css since no existing class covers that specific split.
 */
export function AppsCreatePage() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    function goToRegistry() {
        navigate('/console/apps');
    }

    // EAA-1709: the create response may carry a one-time `<code>-api` secret or a
    // failed provisioning step. Navigating away at once would drop the secret for
    // good, so the page stays until the operator dismisses the result.
    const provisioningResult = useProvisioningResult(goToRegistry);
    const vm = useCreateAppForm(provisioningResult.handleResult);

    function handleSubmit(event: FormEvent) {
        event.preventDefault();
        vm.onSubmit(vm.canSubmit, vm.createMutation.isPending);
    }

    function handleCancel() {
        if (vm.createMutation.isPending) return;
        vm.reset();
        goToRegistry();
    }

    function fieldError(key: string): string | null {
        return vm.fieldErrors[key]?.[0] ?? null;
    }

    // EAA-1846: the design draws this trail in the TOPBAR (confirmed by
    // reading the raw design HTML, not a screenshot) — see
    // `ConsoleBreadcrumbContext`'s own doc for why that lives outside this
    // page. The in-body `← Danh mục ứng dụng` back-link this replaced is
    // redundant now that the middle segment is itself a working link.
    useConsoleBreadcrumb([
        { label: t('console.apps.page.breadcrumbParent') },
        { label: t('console.apps.page.title'), to: '/console/apps' },
        { label: t('console.apps.createPage.breadcrumbCurrent') },
    ]);

    return (
        <main className="portal-admin portal-console-page apps-create-page">
            <PageHeader
                title={t('console.apps.createPage.title')}
                subtitle={
                    <Trans
                        i18nKey="console.apps.createPage.subtitle"
                        components={{ bold: <strong /> }}
                    />
                }
                actions={
                    <>
                        <Button onClick={handleCancel} disabled={vm.createMutation.isPending}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            form="portal-apps-create-form"
                            disabled={!vm.canSubmit || vm.createMutation.isPending}
                        >
                            {vm.createMutation.isPending
                                ? t('console.apps.createModal.submittingLabel')
                                : t('console.apps.page.addAppButton')}
                        </Button>
                    </>
                }
            />

            <div className={styles.grid}>
                <form
                    id="portal-apps-create-form"
                    className={styles.gridMain}
                    onSubmit={handleSubmit}
                >
                    <SectionCard title={t('console.apps.createPage.infoSectionTitle')}>
                        <div className="portal-admin-form">
                            <FormField
                                label={t('console.apps.createPage.nameLabel')}
                                error={fieldError('name')}
                            >
                                <Input
                                    value={vm.scalars.name}
                                    onChange={(e) => vm.setScalar('name', e.target.value)}
                                    placeholder={t('console.apps.createModal.namePlaceholder')}
                                    required
                                />
                            </FormField>

                            <FormField
                                label={t('console.apps.createModal.codeLabel')}
                                hint={t('console.apps.createPage.codeHint')}
                                error={vm.codeError ?? fieldError('code')}
                            >
                                <Input
                                    className="apps-create-mono-input"
                                    value={vm.scalars.code}
                                    onChange={(e) => vm.setScalar('code', e.target.value)}
                                    onBlur={vm.handleCodeBlur}
                                    placeholder={t('console.apps.createModal.codePlaceholder')}
                                    required
                                />
                            </FormField>

                            <FormField
                                label={t('console.apps.createModal.appTypeLabel')}
                                hint={t('console.apps.createPage.appTypeHint')}
                            >
                                <Select
                                    value={vm.scalars.app_type}
                                    onChange={(value) => vm.setScalar('app_type', value)}
                                    options={APP_TYPE_OPTIONS.map((option) => ({
                                        value: option.value,
                                        label: t(option.labelKey),
                                    }))}
                                />
                            </FormField>

                            <FormField
                                label={t('console.apps.createPage.audienceLabel')}
                                hint={t('console.apps.createPage.audienceHint')}
                                error={fieldError('audience')}
                            >
                                <Input
                                    value={vm.scalars.audience}
                                    onChange={(e) => vm.setScalar('audience', e.target.value)}
                                    placeholder={t('console.apps.createModal.audiencePlaceholder')}
                                />
                            </FormField>

                            <FormField
                                label={t('console.apps.createPage.webUrlLabel')}
                                error={vm.webUrlError ?? fieldError('web_url')}
                            >
                                <Input
                                    value={vm.scalars.web_url}
                                    onChange={(e) => vm.setScalar('web_url', e.target.value)}
                                    onBlur={() => vm.markTouched('web_url')}
                                    placeholder={t('console.apps.createModal.webUrlPlaceholder')}
                                />
                            </FormField>

                            <FormField
                                label={t('console.apps.createPage.apiBaseLabel')}
                                error={fieldError('api_base')}
                            >
                                <Input
                                    value={vm.scalars.api_base}
                                    onChange={(e) => vm.setScalar('api_base', e.target.value)}
                                    placeholder={t('console.apps.createModal.apiBasePlaceholder')}
                                />
                            </FormField>

                            {/* EAA-1846: kept off the screenshot's visible fold, but
                                every field the modal had must still work here — see
                                this file's doc comment. */}
                            <FormField
                                label={t('console.apps.createModal.iconUrlLabel')}
                                error={vm.iconUrlError ?? fieldError('icon_url')}
                            >
                                <Input
                                    value={vm.scalars.icon_url}
                                    onChange={(e) => vm.setScalar('icon_url', e.target.value)}
                                    onBlur={() => vm.markTouched('icon_url')}
                                    placeholder={t('console.apps.createModal.iconUrlPlaceholder')}
                                />
                            </FormField>

                            <FormField
                                label={t('console.apps.createPage.descriptionLabel')}
                                error={fieldError('description')}
                                className="apps-create-field--full"
                            >
                                <Input.TextArea
                                    value={vm.scalars.description}
                                    onChange={(e) => vm.setScalar('description', e.target.value)}
                                    rows={2}
                                />
                            </FormField>
                        </div>
                    </SectionCard>

                    <SectionCard title={t('console.apps.createPage.ownerSectionTitle')}>
                        <div className="portal-admin-form">
                            {/* EAA-1846: the screenshot renders this as a
                                select/combobox with a pre-filled value, but
                                `owner_name` is a free-text field on
                                `AdminRegistryApp` with no directory lookup
                                endpoint behind it — inventing a combobox with no
                                real data source would be worse than the plain
                                text field the modal already shipped and
                                validates, so this stays a text `Input`. */}
                            <FormField
                                label={t('console.apps.createPage.ownerNameLabel')}
                                error={fieldError('owner_name')}
                            >
                                <Input
                                    value={vm.scalars.owner_name}
                                    onChange={(e) => vm.setScalar('owner_name', e.target.value)}
                                    placeholder={t('console.apps.createModal.ownerNamePlaceholder')}
                                />
                            </FormField>

                            <FormField
                                label={t('console.apps.createPage.ownerEmailLabel')}
                                error={vm.ownerEmailError ?? fieldError('owner_email')}
                            >
                                <Input
                                    type="email"
                                    value={vm.scalars.owner_email}
                                    onChange={(e) => vm.setScalar('owner_email', e.target.value)}
                                    onBlur={() => vm.markTouched('owner_email')}
                                    placeholder={t(
                                        'console.apps.createModal.ownerEmailPlaceholder'
                                    )}
                                />
                            </FormField>

                            {/* EAA-1846: a section-level note, not one field's hint — stays
                                a plain paragraph rather than FormField, which is scoped to
                                exactly one labelled control. */}
                            <p className="portal-admin-hint">
                                {t('console.apps.createPage.ownerHint')}
                            </p>
                        </div>
                    </SectionCard>

                    <SectionCard title={t('console.apps.createPage.oidcSectionTitle')}>
                        <div className="portal-admin-form">
                            <AppUriListField
                                legend={t('console.apps.createModal.redirectUrisLegend')}
                                required
                                values={vm.redirectUris}
                                onChange={vm.setRedirectUris}
                                onBlur={() => vm.markTouched('redirect_uris')}
                                error={vm.redirectError ?? fieldError('redirect_uris')}
                                placeholder={t('console.apps.createModal.redirectUriPlaceholder')}
                                addLabel={t('console.apps.createModal.addUriButton')}
                            />

                            <AppUriListField
                                legend={t('console.apps.createModal.postLogoutUrisLegend')}
                                values={vm.postLogoutUris}
                                onChange={vm.setPostLogoutUris}
                                onBlur={() => vm.markTouched('post_logout_redirect_uris')}
                                error={
                                    vm.postLogoutError ?? fieldError('post_logout_redirect_uris')
                                }
                                placeholder={t('console.apps.createModal.webUrlPlaceholder')}
                                addLabel={t('console.apps.createModal.addUriButton')}
                            />

                            <OptionCardGroup
                                legend={t('console.apps.createModal.orgScopeLegend')}
                                footer={<FormFieldMessage error={fieldError('org_scope_mode')} />}
                            >
                                <OptionCard
                                    inputType="radio"
                                    name="org_scope_mode"
                                    selected={vm.scalars.org_scope_mode === 'selected_orgs'}
                                    onSelect={() => vm.setScalar('org_scope_mode', 'selected_orgs')}
                                    title={t('console.apps.createModal.orgScopeSelectedTitle')}
                                    description={t(
                                        'console.apps.createModal.orgScopeSelectedDescription'
                                    )}
                                />
                                <OptionCard
                                    inputType="radio"
                                    name="org_scope_mode"
                                    selected={vm.scalars.org_scope_mode === 'all_orgs'}
                                    onSelect={() => vm.setScalar('org_scope_mode', 'all_orgs')}
                                    title={t('console.apps.scope.allOrgs')}
                                    description={t(
                                        'console.apps.createModal.orgScopeAllDescription'
                                    )}
                                />
                            </OptionCardGroup>

                            <OptionCardGroup
                                legend={t('console.apps.createModal.roleManagementLegend')}
                                footer={
                                    <FormFieldMessage
                                        error={fieldError('supports_role_management')}
                                    />
                                }
                            >
                                <OptionCard
                                    inputType="checkbox"
                                    selected={vm.scalars.supports_role_management}
                                    onSelect={() =>
                                        vm.setScalar(
                                            'supports_role_management',
                                            !vm.scalars.supports_role_management
                                        )
                                    }
                                    title={t('console.apps.createModal.roleManagementTitle')}
                                    description={
                                        <>
                                            {t(
                                                'console.apps.createModal.roleManagementDescriptionPrefix'
                                            )}
                                            <code>POST /api/v1/management/roles</code>
                                            {t(
                                                'console.apps.createModal.roleManagementDescriptionSuffix'
                                            )}
                                        </>
                                    }
                                />
                            </OptionCardGroup>

                            {/* EAA-1846: a whole-form error, not one field's — stays a
                                plain paragraph, same reasoning as the owner-section hint. */}
                            {vm.formError && <p className="portal-admin-error">{vm.formError}</p>}
                        </div>
                    </SectionCard>
                </form>

                <aside className={styles.gridSide}>
                    <SectionCard title={t('console.apps.createPage.afterSaveTitle')}>
                        <ol className={styles.afterSaveList}>
                            {AFTER_SAVE_STEP_KEYS.map((step) => (
                                <li key={step} className={styles.afterSaveItem}>
                                    <span className={styles.afterSaveIndex} aria-hidden="true">
                                        {step}
                                    </span>
                                    <span className={styles.afterSaveBody}>
                                        <strong className={styles.afterSaveTitle}>
                                            {t(`console.apps.createPage.afterSaveStep${step}Title`)}
                                        </strong>
                                        <span className={styles.afterSaveDescription}>
                                            {t(
                                                `console.apps.createPage.afterSaveStep${step}Description`
                                            )}
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </SectionCard>

                    <Callout tone="warning">
                        <Trans
                            i18nKey="console.apps.createPage.warningCallout"
                            components={{ bold: <strong /> }}
                        />
                    </Callout>
                </aside>
            </div>

            <AppProvisioningResultModal
                provisioning={provisioningResult.provisioning}
                onClose={provisioningResult.close}
            />
        </main>
    );
}

export default AppsCreatePage;
